import { Link } from 'react-router-dom';
import { Twitter, Linkedin, Github, Youtube, Book, ArrowRight } from 'lucide-react';
import { footerLinks, site } from '@/data/content';
import PaymentMethods from './PaymentMethods';
import { useCookieConsent } from './CookieConsent';
import { useEffect, useState } from 'react';

export default function Footer() {
  const { openModal } = useCookieConsent();
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);

  useEffect(() => {
    // Load ZuriChat widget script
    const script = document.createElement('script');
    script.src = 'https://media.getzurihub.com/widget.js?v=' + Date.now();
    script.async = true;
    script.setAttribute('data-workspace', 'ws_mr9ll0xx_1ctlv5zs');
    script.setAttribute('data-api', 'https://backend.getzurihub.com');
    script.setAttribute('data-name', 'ZuriHub Support');
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      setSubscribed(true);
      setEmail('');
      setTimeout(() => setSubscribed(false), 3000);
    }
  };

  return (
    <footer className="bg-slate-900 text-slate-300">
      {/* Main Footer Content */}
      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
        {/* Brand Section */}
        <div className="mb-12 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div className="max-w-sm">
            <Link to="/" className="inline-block">
              <img
                src="https://media.getzurihub.com/Jayrah%20Assets/ZuriHub%20Logo.png"
                alt={site.name}
                className="h-8 w-auto object-contain"
              />
            </Link>
            <p className="mt-4 text-sm leading-relaxed text-slate-400">
              {site.tagline}
            </p>
            <p className="mt-2 text-sm text-slate-500">
              Unify live chat, tickets, knowledge base, and AI in one powerful platform.
            </p>

            {/* Social Links */}
            <div className="mt-6 flex gap-4">
              <a
                href={site.twitter}
                target="_blank"
                rel="noreferrer"
                className="text-slate-400 transition-colors hover:text-white"
                aria-label="X"
              >
                <Twitter className="h-5 w-5" />
              </a>
              <a
                href={site.linkedin}
                target="_blank"
                rel="noreferrer"
                className="text-slate-400 transition-colors hover:text-white"
                aria-label="LinkedIn"
              >
                <Linkedin className="h-5 w-5" />
              </a>
              <a
                href={site.github}
                target="_blank"
                rel="noreferrer"
                className="text-slate-400 transition-colors hover:text-white"
                aria-label="GitHub"
              >
                <Github className="h-5 w-5" />
              </a>
              <a
                href={site.youtube}
                target="_blank"
                rel="noreferrer"
                className="text-slate-400 transition-colors hover:text-white"
                aria-label="YouTube"
              >
                <Youtube className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Newsletter */}
          <div className="max-w-md">
            <h3 className="text-lg font-semibold text-white">
              Subscribe to our newsletter
            </h3>
            <p className="mt-2 text-sm text-slate-400">
              Get the latest product updates and customer success stories.
            </p>
            <form onSubmit={handleSubscribe} className="mt-4 flex gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-1 rounded-md border border-slate-700 bg-slate-800 px-4 py-2 text-sm text-white placeholder-slate-500 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                required
              />
              <button
                type="submit"
                className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              >
                {subscribed ? 'Subscribed!' : 'Subscribe'}
                {!subscribed && <ArrowRight className="h-4 w-4" />}
              </button>
            </form>
          </div>
        </div>

        {/* 5-Column Grid for Links */}
        <div className="grid grid-cols-2 gap-8 md:grid-cols-3 lg:grid-cols-5">
          {Object.entries(footerLinks).map(([group, links]) => (
            <div key={group}>
              <h4 className="text-sm font-semibold text-white">
                {group === 'tools' ? 'Tools & Channels' : group.charAt(0).toUpperCase() + group.slice(1)}
              </h4>
              <ul className="mt-4 space-y-3">
                {links.map((link) => (
                  <li key={link.path + link.label}>
                    {link.path === '/cookie-notice' ? (
                      <button
                        onClick={openModal}
                        className="text-sm text-slate-400 transition-colors hover:text-white"
                      >
                        {link.label}
                      </button>
                    ) : link.path.startsWith('http') || link.external ? (
                      <a
                        href={link.path}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 text-sm text-slate-400 transition-colors hover:text-white"
                      >
                        {link.label === 'Help Centre' && <Book className="h-3.5 w-3.5" />}
                        {link.label}
                      </a>
                    ) : (
                      <Link to={link.path} className="text-sm text-slate-400 transition-colors hover:text-white">
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800 bg-slate-950">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            {/* Copyright */}
            <p className="text-sm text-slate-500">
              © {new Date().getFullYear()} {site.name}. All rights reserved.
            </p>

            {/* Legal Links & Payment Methods */}
            <div className="flex flex-wrap items-center gap-6">
              <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                <Link to="/privacy" className="transition-colors hover:text-slate-300">
                  Privacy Policy
                </Link>
                <Link to="/terms" className="transition-colors hover:text-slate-300">
                  Terms of Service
                </Link>
                <button onClick={openModal} className="transition-colors hover:text-slate-300">
                  Cookie Preferences
                </button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">We accept:</span>
                <PaymentMethods />
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
